import * as React from 'react';
import { useClipboard } from 'use-clipboard-copy';
import { FacebookShareButton } from 'react-share';
import styled from 'styled-components';

import { MONTH_NAME_FORMAT } from '@doltech/utils/lib/constants';
import { formatDate } from '@doltech/utils/lib/date';
import { flexGap } from '@doltech/utils/lib/css-style';
import { getLengthEnumTextByKey } from '@doltech/utils/lib/enum/LengthEnum';
import { IconSystemV2LogoFacebook2 } from '@doltech/icons/SystemV2/IconSystemV2LogoFacebook2';
import { IconSystemV2EssentialLinkChain } from '@doltech/icons/SystemV2/IconSystemV2EssentialLinkChain';

import { colorsV2 } from '../../colors-v2';
import { ResponsiveTypography } from '../../Typography/v3/ResponsiveTypography';
import { DotDivider } from '../../Divider/DotDivider';
import { UserInfoCardLayout } from '../../User/UserInfoCardLayout/UserInfoCardLayout';
import { DolTooltip } from '../../Tooltip/DolTooltip';
import { ButtonGeneral } from '../../Button/ButtonGeneral/ButtonGeneral';

const SubInfo = styled.div`
  display: flex;
  align-items: center;
`;

const Social = styled.div`
  display: flex;
  align-items: center;
  ${flexGap(4)}
`;

const Info = styled.div``;

const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-end;
  column-gap: 32px;
`;

interface BlogPageElementMetadataProps {
  pageUrl?: string;
  author?: {
    name?: string;
    avatarUrl?: string;
  };
  publishedDate?: string;
  blogLength?: string;
  withShareAction?: boolean;
}

export const BlogPageElementMetadata = (props: BlogPageElementMetadataProps) => {
  const { pageUrl = '/', author, publishedDate, blogLength, withShareAction = false } = props;

  const { copy, copied } = useClipboard({
    copiedTimeout: 1500, // timeout duration in milliseconds
  });

  return (
    <Container>
      <Info>
        <UserInfoCardLayout
          text={
            <ResponsiveTypography.Paragraph
              variant="medium/16-24"
              color="neutral180"
              ellipsis={{ tooltip: true }}
            >
              {author?.name}
            </ResponsiveTypography.Paragraph>
          }
          subText={
            <SubInfo>
              <ResponsiveTypography.Paragraph
                variant="regular/14-16"
                color="neutral120"
              >
                {formatDate(publishedDate, MONTH_NAME_FORMAT)}
              </ResponsiveTypography.Paragraph>
              <DotDivider type="horizontal" dotSize={3} space={8} background={colorsV2.neutral40} />
              <ResponsiveTypography.Paragraph
                variant="regular/14-16"
                color="neutral120"
              >
                {getLengthEnumTextByKey(blogLength)}
              </ResponsiveTypography.Paragraph>
            </SubInfo>

          }
          avatarUrl={author?.avatarUrl}
          avatarSize="40px"
        />
      </Info>
      {withShareAction && (
        <Social className="social-actions">
          <DolTooltip
            trigger={['click']}
            title="Đã sao chép link"
            placement="top"
            visible={copied}
          >
            <ButtonGeneral
              type="secondary-filled"
              size="x-small"
              iconLeft={<IconSystemV2EssentialLinkChain />}
              text="Copy link"
              onClick={() => copy(pageUrl)}
              transparent
            />
          </DolTooltip>
          <FacebookShareButton key="facebook-share" url={pageUrl}>
            <ButtonGeneral
              type="secondary-filled"
              size="x-small"
              iconLeft={<IconSystemV2LogoFacebook2 />}
              text="Share"
              transparent
            />
          </FacebookShareButton>
        </Social>
      )}
    </Container>
  );
};
